import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Clock, X } from "lucide-react";
import { adminApi, apiError } from "../adminApi";
import { Panel, Pill, inputClass, buttonClass } from "../components/Bits";
import { HarvestShell, useHarvestHeader } from "./HarvestShell";
import { formatStamp } from "../../lib/harvestDates";

const STATUS_TONE = {
  pending: "gold", approved: "sage", executed: "sage",
  rejected: "terra", expired: "neutral", failed: "terra",
};
const CHECK_LABELS = {
  gift_cap: "Gift cap", footer: "Compliance footer", suppression: "Suppression list",
  window: "Ship window", pause: "Pause band",
};

const DecisionForm = ({ approval, onDone }) => {
  const [confirm, setConfirm] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const needsAmount = approval.est_cost_usd > 100;

  const decide = async (decision) => {
    setBusy(true);
    try {
      await adminApi.post(`/crm/harvest/approvals/${approval.id}/decide`, {
        decision,
        confirm: decision === "approve" ? confirm : undefined,
        amount_confirm: decision === "approve" && needsAmount ? Number(amount) : undefined,
        note: note || undefined,
      });
      toast.success(decision === "approve"
        ? "Recorded. Now run this step in Apollo or Thanks.io, then mark it executed."
        : "Request rejected");
      onDone();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 grid gap-3 sm:grid-cols-4" data-testid={`harvest-decision-${approval.id}`}>
      <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Type APPROVE"
        data-testid={`harvest-confirm-${approval.id}`} className={inputClass} />
      {needsAmount ? (
        <input value={amount} onChange={(e) => setAmount(e.target.value)}
          placeholder={`Re-type ${approval.est_cost_usd.toFixed(2)}`}
          data-testid={`harvest-amount-${approval.id}`} className={inputClass} />
      ) : null}
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (required to reject)"
        data-testid={`harvest-note-${approval.id}`} className={inputClass} />
      <div className="flex gap-2">
        <button type="button" disabled={busy} onClick={() => decide("approve")} className={buttonClass}
          data-testid={`harvest-approve-${approval.id}`}>
          <Check className="h-3.5 w-3.5" /> Approve
        </button>
        <button type="button" disabled={busy} onClick={() => decide("reject")}
          data-testid={`harvest-reject-${approval.id}`}
          className="inline-flex items-center gap-2 rounded-full border border-terra/40 px-4 py-2.5 text-xs text-terra transition-colors duration-200 hover:bg-terra/10">
          <X className="h-3.5 w-3.5" /> Reject
        </button>
      </div>
    </div>
  );
};

export default function AdminHarvestApprovals() {
  const { settings, pending } = useHarvestHeader();
  const [rows, setRows] = useState([]);

  const load = () => adminApi.get("/crm/harvest/approvals")
    .then(({ data }) => setRows(data.approvals))
    .catch((error) => toast.error(apiError(error)));

  useEffect(() => { load(); }, []);

  const markExecuted = async (id) => {
    try {
      await adminApi.post(`/crm/harvest/approvals/${id}/mark-executed`, { result: "Done by hand" });
      toast.success("Marked executed");
      load();
    } catch (error) { toast.error(apiError(error)); }
  };

  return (
    <HarvestShell settings={settings} pending={pending}>
      <Panel title={`Approvals · ${rows.filter((r) => r.status === "pending").length} pending`}
        testid="harvest-approvals-panel">
        <p className="text-xs text-cream/45">
          Recorded only: approving never calls Apollo, Thanks.io or anything else. Every decision is
          written to the bot audit log. Requests expire after 72 hours.
        </p>
        <div className="mt-5 space-y-4">
          {rows.map((approval) => (
            <div key={approval.id} data-testid={`harvest-approval-${approval.id}`}
              className="rounded-lg border border-cream/10 bg-cream/[0.03] p-4">
              <div className="flex flex-wrap items-center gap-3">
                <Pill tone={STATUS_TONE[approval.status]}>{approval.status}</Pill>
                <span className="text-sm text-cream">{approval.type}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-cream/40">
                  ${Number(approval.est_cost_usd || 0).toFixed(2)} · {approval.est_credits || 0} credits
                </span>
                <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-cream/35">
                  <Clock className="h-3 w-3" /> {formatStamp(approval.requested_at)} · {approval.requested_by}
                </span>
              </div>

              <pre className="mt-3 max-h-32 overflow-auto rounded border border-cream/5 bg-black/20 p-3 text-[11px] text-cream/60"
                data-testid={`harvest-payload-${approval.id}`}>
                {JSON.stringify(approval.payload, null, 2)}
              </pre>

              <div className="mt-3 flex flex-wrap gap-2" data-testid={`harvest-checks-${approval.id}`}>
                {Object.entries(approval.checks || {}).map(([key, ok]) => (
                  <Pill key={key} tone={ok ? "sage" : "terra"}>
                    {CHECK_LABELS[key] || key}: {ok ? "clear" : "blocked"}
                  </Pill>
                ))}
              </div>

              {approval.status === "pending" ? (
                <DecisionForm approval={approval} onDone={load} />
              ) : null}

              {approval.status === "approved" ? (
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <p className="text-xs text-gold">
                    Do this step in Apollo/Thanks.io, then mark it executed.
                  </p>
                  <button type="button" onClick={() => markExecuted(approval.id)}
                    data-testid={`harvest-executed-${approval.id}`}
                    className="rounded-full border border-gold/40 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-gold hover:bg-gold/10">
                    Mark executed
                  </button>
                </div>
              ) : null}

              {approval.decided_by ? (
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-cream/35"
                  data-testid={`harvest-decided-${approval.id}`}>
                  {approval.status} by {approval.decided_by} · {formatStamp(approval.decided_at)}
                  {approval.note ? ` · ${approval.note}` : ""}
                </p>
              ) : null}
            </div>
          ))}
          {!rows.length ? (
            <p className="text-sm text-cream/40" data-testid="harvest-approvals-empty">
              No approval requests yet. Mail waves and paid steps land here before anything is ordered.
            </p>
          ) : null}
        </div>
      </Panel>
    </HarvestShell>
  );
}
